import React, { useState } from 'react';
import {
  Button,
  Descriptions,
  Divider,
  Drawer,
  Progress,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
// Api
import { recruitmentClient } from 'api/Recruitment/client';
import { GET_CSV_DOWNLOAD_URL } from 'api/Recruitment/queries';
// Types
import {
  GetCsvDownloadUrl,
  GetCsvDownloadUrlVariables,
} from 'api/Recruitment/types/GetCsvDownloadUrl';
import {
  ListImportCsvJobs_listImportCsvJobs_items,
  ListImportCsvJobs_listImportCsvJobs_items_stats_counters,
  ListImportCsvJobs_listImportCsvJobs_items_stats_llm_byModel,
} from 'api/Recruitment/types/ListImportCsvJobs';
// UI
import { errorNotification } from 'ui/Notification';
// Helpers
import {
  formatImportJobDate,
  getImportJobFilename,
  getImportJobStatusColor,
} from '../../helpers';
// Styles
import styles from './JobDetailsDrawer.module.scss';

const { Text, Title } = Typography;

type JobDetailsDrawerProps = {
  job: ListImportCsvJobs_listImportCsvJobs_items | null;
  onClose: () => void;
};

const counterColumns: ColumnsType<ListImportCsvJobs_listImportCsvJobs_items_stats_counters> =
  [
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
      render: (label: string | null) => label || '-',
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (value: number | null) => value ?? '-',
    },
  ];

const llmColumns: ColumnsType<ListImportCsvJobs_listImportCsvJobs_items_stats_llm_byModel> =
  [
    { title: 'Model', dataIndex: 'model', key: 'model' },
    { title: 'Input', dataIndex: 'input', key: 'input' },
    { title: 'Cached', dataIndex: 'cached', key: 'cached' },
    { title: 'Output', dataIndex: 'output', key: 'output' },
    { title: 'Total', dataIndex: 'total', key: 'total' },
  ];

const JobDetailsDrawer = ({
  job,
  onClose,
}: JobDetailsDrawerProps): JSX.Element => {
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const isFailed = job?.status?.startsWith('FAILED');
  const counters = job?.stats?.counters || [];
  const llmUsage = job?.stats?.llm?.byModel || [];

  const handleDownloadOriginalCsv = async () => {
    if (!job) return;

    try {
      setIsDownloading(true);
      const result = await recruitmentClient.query<
        GetCsvDownloadUrl,
        GetCsvDownloadUrlVariables
      >({
        fetchPolicy: 'network-only',
        query: GET_CSV_DOWNLOAD_URL,
        variables: { jobId: job.jobId },
      });
      const downloadUrl = result.data?.getCsvDownloadUrl;

      if (!downloadUrl) {
        errorNotification('CSV download could not be started.');
        return;
      }

      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      errorNotification('CSV download could not be started.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Drawer
      destroyOnClose
      onClose={onClose}
      title="Import Job Details"
      visible={!!job}
      width={480}
    >
      {job && (
        <>
          <Title level={5}>Basic Info</Title>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Submitted At">
              {formatImportJobDate(job.createdAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getImportJobStatusColor(job.status)}>
                {job.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Progress">
              <Progress percent={job.progressPercent || 0} size="small" />
            </Descriptions.Item>
            {job.completedAt && (
              <Descriptions.Item label="Completed At">
                {formatImportJobDate(job.completedAt)}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Filename">
              {getImportJobFilename(job.s3Key)}
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <Title level={5}>Actions</Title>
          <Button loading={isDownloading} onClick={handleDownloadOriginalCsv}>
            Download Original CSV
          </Button>

          {job.stats && (
            <>
              <Divider />

              <Title level={5}>Stats</Title>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Total Rows">
                  {job.stats.totalRows ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Saved">
                  {job.stats.saved ?? '-'}
                </Descriptions.Item>
              </Descriptions>

              <Table<ListImportCsvJobs_listImportCsvJobs_items_stats_counters>
                columns={counterColumns}
                dataSource={counters}
                pagination={false}
                rowKey={(counter) =>
                  `${counter.section}-${counter.key}-${counter.label}`
                }
                size="small"
                className={styles.table}
              />

              {llmUsage.length > 0 && (
                <Table<ListImportCsvJobs_listImportCsvJobs_items_stats_llm_byModel>
                  columns={llmColumns}
                  dataSource={llmUsage}
                  pagination={false}
                  rowKey={(usage) => usage.model || 'unknown-model'}
                  size="small"
                  className={styles.table}
                />
              )}
            </>
          )}

          {isFailed && (
            <>
              <Divider />

              <Title level={5}>Error Info</Title>
              {job.failureError && (
                <pre className={styles.errorCode}>{job.failureError}</pre>
              )}
              {job.failureCause && (
                <Text type="secondary">{job.failureCause}</Text>
              )}
            </>
          )}
        </>
      )}
    </Drawer>
  );
};

export default JobDetailsDrawer;
